import { useEffect, useLayoutEffect, useRef, useState, type ReactElement } from 'react'
import { createPortal } from 'react-dom'
import './tooltips.css'

// EL tooltip de camp: uno solo, montado una vez (App.tsx), que atiende a toda la
// app por delegación de eventos.
//
// POR QUÉ una capa y no un <Tooltip> que envuelva cada botón: camp tiene ~270
// aria-label y ~300 title= repartidos en ochenta archivos que otros equipos están
// tocando esta misma semana. Envolverlos uno por uno era un cambio enorme, con
// conflictos seguros, y el siguiente botón que alguien agregara sin envolver
// volvería a salir sin tooltip. Con la capa, la regla se cumple sola:
//
//   1. `data-tip="texto"` (opcional `data-tip-atajo="⌘["`) → ese texto. Es la
//      forma de escribir código NUEVO. `data-tip=""` apaga el tooltip.
//   2. Botón o enlace de SOLO ICONO con aria-label → su aria-label. Es exactamente
//      lo que la persona con lector de pantalla ya oye; ahora también se ve.
//   3. `title=` → su texto, con NUESTRO estilo: al pasar el ratón la capa guarda
//      el title y lo devuelve al salir, para que el nativo (gris, lento, distinto
//      en cada sistema) no aparezca encima del nuestro. Con teclado no se toca:
//      el title nativo nunca sale con el foco.
//
// Accesible: aparece con ratón (retardo corto) y con foco de TECLADO; nunca en
// táctil (un tooltip ahí tapa lo que el dedo acaba de tocar); Esc lo cierra
// (WCAG 1.4.13); se esconde al hacer clic, al desplazar y cuando el control abre
// su propio menú. `data-tip-off` en un contenedor lo apaga en toda esa zona.

const SELECTOR = [
  '[data-tip]',
  '[title]',
  '[data-tip-titulo]',
  'button[aria-label]',
  'a[href][aria-label]',
  '[role="button"][aria-label]',
  '[role="tab"][aria-label]',
].join(',')

export const RETARDO_RATON = 400
export const RETARDO_FOCO = 120
/** Si otro tooltip se cerró hace menos que esto, el siguiente sale sin esperar. */
const CALENTAMIENTO = 450
const SEPARACION = 8
const MARGEN = 8

interface Tip {
  texto: string
  atajo?: string
  /** El texto dice algo que el nombre accesible no dice: se enlaza con aria-describedby. */
  describe: boolean
}

/** ¿El control no tiene texto visible? (se ignora lo aria-hidden y lo .sr-only) */
export function soloIcono(el: Element): boolean {
  const recorrer = (n: Node): boolean => {
    if (n.nodeType === Node.TEXT_NODE) return !(n.textContent ?? '').trim()
    if (!(n instanceof Element)) return true
    if (n.getAttribute('aria-hidden') === 'true' || n.classList.contains('sr-only')) return true
    if (n.tagName === 'svg' || n.tagName === 'IMG') return true
    return Array.from(n.childNodes).every(recorrer)
  }
  return Array.from(el.childNodes).every(recorrer)
}

/**
 * ¿Es un velo y no un botón? Los menús de la app se cierran con un <button>
 * invisible de pantalla completa ("Cerrar menú"): técnicamente es un botón sin
 * texto con aria-label, pero un tooltip ahí saldría flotando en medio de la
 * pantalla. Nada que ocupe media pantalla es "un botón de icono".
 */
function esVelo(el: Element): boolean {
  const r = el.getBoundingClientRect()
  const area = (window.innerWidth || 0) * (window.innerHeight || 0)
  return area > 0 && r.width * r.height > area / 2
}

/** Qué tooltip le toca a un elemento, o null si ninguno. */
export function tipDe(el: Element): Tip | null {
  if (el.closest('[data-tip-off]')) return null
  // En un iframe o un reproductor el title es su NOMBRE accesible, no una ayuda
  // (el visor de Drive, la presentación): un tooltip encima taparía el contenido.
  if (el.matches('iframe, embed, object, video, audio')) return null
  const atajo = el.getAttribute('data-tip-atajo')?.trim() || undefined
  const nombre = el.getAttribute('aria-label')?.trim() ?? ''
  if (el.hasAttribute('data-tip')) {
    const texto = el.getAttribute('data-tip')?.trim() ?? ''
    return texto ? { texto, atajo, describe: Boolean(atajo) || texto !== nombre } : null
  }
  const titulo = (el.getAttribute('title') ?? el.getAttribute('data-tip-titulo') ?? '').trim()
  // Un title vacío (o el que dejamos al adoptarlo) no es un tooltip.
  if (titulo) return { texto: titulo, atajo, describe: false }
  if (nombre && el.matches('button, a[href], [role="button"], [role="tab"]') && soloIcono(el) && !esVelo(el)) {
    return { texto: nombre, atajo, describe: Boolean(atajo) }
  }
  return null
}

/** El elemento más cercano (desde el que recibió el evento hacia arriba) que tenga tooltip. */
function objetivo(desde: EventTarget | null): Element | null {
  let el = desde instanceof Element ? desde.closest(SELECTOR) : null
  while (el) {
    if (el.closest('.tip')) return null
    if (tipDe(el)) return el
    el = el.parentElement?.closest(SELECTOR) ?? null
  }
  return null
}

interface Visible extends Tip {
  el: Element
}

let secuencia = 0

export function CapaTooltips(): ReactElement | null {
  const [visible, setVisible] = useState<Visible | null>(null)
  const tipRef = useRef<HTMLDivElement>(null)
  const idRef = useRef(`tip-${++secuencia}`)

  useEffect(() => {
    let timer = 0
    let actual: Element | null = null
    let mostrado = false
    let ultimoCierre = 0
    let ultimaFuePuntero = false
    /** El control que se acaba de picar: no vuelve a mostrar tooltip hasta que el puntero salga. */
    let suprimido: Element | null = null
    /** title que guardamos mientras el ratón está encima (y aria-label prestado, si hizo falta). */
    let adoptado: { el: Element; titulo: string; prestoNombre: boolean } | null = null
    let describedbyPrevio: string | null = null

    const adoptarTitle = (el: Element) => {
      const titulo = el.getAttribute('title')
      if (!titulo) return
      // El title era el NOMBRE de un botón de solo icono: mientras lo guardamos se
      // presta como aria-label para no dejar al control sin nombre.
      const prestoNombre = !el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby') && soloIcono(el)
      if (prestoNombre) el.setAttribute('aria-label', titulo)
      el.setAttribute('data-tip-titulo', titulo)
      el.removeAttribute('title')
      adoptado = { el, titulo, prestoNombre }
    }
    const devolverTitle = () => {
      if (!adoptado) return
      const { el, titulo, prestoNombre } = adoptado
      adoptado = null
      el.removeAttribute('data-tip-titulo')
      // Si React escribió otro title mientras tanto, ese manda.
      if (!el.hasAttribute('title')) el.setAttribute('title', titulo)
      if (prestoNombre && el.getAttribute('aria-label') === titulo) el.removeAttribute('aria-label')
    }

    const quitarDescribedby = (el: Element | null) => {
      if (!el || describedbyPrevio === null) return
      if (describedbyPrevio) el.setAttribute('aria-describedby', describedbyPrevio)
      else el.removeAttribute('aria-describedby')
      describedbyPrevio = null
    }

    const ocultar = () => {
      window.clearTimeout(timer)
      timer = 0
      if (mostrado) ultimoCierre = Date.now()
      mostrado = false
      quitarDescribedby(actual)
      devolverTitle()
      actual = null
      setVisible(null)
    }

    const programar = (el: Element, retardo: number) => {
      window.clearTimeout(timer)
      const caliente = Date.now() - ultimoCierre < CALENTAMIENTO
      timer = window.setTimeout(
        () => {
          timer = 0
          if (actual !== el || !el.isConnected) return
          // El control abrió su propio menú: el tooltip lo taparía.
          if (el.getAttribute('aria-expanded') === 'true') return
          const tip = tipDe(el)
          if (!tip) return
          if (tip.describe) {
            describedbyPrevio = el.getAttribute('aria-describedby') ?? ''
            el.setAttribute('aria-describedby', [describedbyPrevio, idRef.current].filter(Boolean).join(' '))
          }
          mostrado = true
          setVisible({ ...tip, el })
        },
        caliente ? 0 : retardo,
      )
    }

    const empezar = (el: Element, retardo: number, porRaton: boolean) => {
      if (actual === el) return
      if (actual) ocultar()
      actual = el
      if (porRaton) adoptarTitle(el)
      programar(el, retardo)
    }

    const alEntrar = (e: PointerEvent) => {
      // Táctil nunca; y tampoco mientras se arrastra algo (tablero, dock).
      if (e.pointerType === 'touch' || (e.buttons ?? 0) !== 0) return
      const el = objetivo(e.target)
      if (!el) {
        if (actual && !actual.contains(e.target as Node)) ocultar()
        return
      }
      if (el === suprimido) return
      empezar(el, RETARDO_RATON, true)
    }
    const alSalir = (e: PointerEvent) => {
      const hacia = e.relatedTarget as Node | null
      if (suprimido && !(hacia && suprimido.contains(hacia))) suprimido = null
      if (!actual) return
      if (hacia && actual.contains(hacia)) return
      // Si va hacia otro control con tooltip, alEntrar se encarga del cambio.
      if (hacia && objetivo(hacia)) return
      ocultar()
    }
    const alPicar = (e: PointerEvent) => {
      ultimaFuePuntero = true
      const el = objetivo(e.target)
      if (el) suprimido = el
      if (actual) ocultar()
    }
    const alTeclear = (e: KeyboardEvent) => {
      ultimaFuePuntero = false
      if (e.key === 'Escape' && actual) ocultar()
    }
    const alEnfocar = (e: FocusEvent) => {
      // Foco que llegó por clic o por toque: no es navegación con teclado.
      if (ultimaFuePuntero) return
      const el = objetivo(e.target)
      if (el && el === e.target) empezar(el, RETARDO_FOCO, false)
    }
    const alDesenfocar = (e: FocusEvent) => {
      if (actual && e.target === actual) ocultar()
    }
    const alDesplazar = () => {
      if (actual) ocultar()
    }

    document.addEventListener('pointerover', alEntrar, true)
    document.addEventListener('pointerout', alSalir, true)
    document.addEventListener('pointerdown', alPicar, true)
    document.addEventListener('keydown', alTeclear, true)
    document.addEventListener('focusin', alEnfocar, true)
    document.addEventListener('focusout', alDesenfocar, true)
    window.addEventListener('scroll', alDesplazar, true)
    window.addEventListener('blur', ocultar)
    return () => {
      ocultar()
      document.removeEventListener('pointerover', alEntrar, true)
      document.removeEventListener('pointerout', alSalir, true)
      document.removeEventListener('pointerdown', alPicar, true)
      document.removeEventListener('keydown', alTeclear, true)
      document.removeEventListener('focusin', alEnfocar, true)
      document.removeEventListener('focusout', alDesenfocar, true)
      window.removeEventListener('scroll', alDesplazar, true)
      window.removeEventListener('blur', ocultar)
    }
  }, [])

  // Posición: arriba del control por defecto (abajo si no cabe, o si el
  // contenedor lo pide con data-tip-lado="abajo", como la barra superior). Se mide
  // en el mismo cuadro, antes de pintar, para que nunca se vea brincar.
  useLayoutEffect(() => {
    const tip = tipRef.current
    if (!visible || !tip) return
    const r = visible.el.getBoundingClientRect()
    const w = tip.offsetWidth
    const h = tip.offsetHeight
    const vw = document.documentElement.clientWidth || window.innerWidth
    const vh = window.innerHeight
    const pide = visible.el.closest('[data-tip-lado]')?.getAttribute('data-tip-lado')
    const cabeArriba = r.top - h - SEPARACION >= MARGEN
    const cabeAbajo = r.bottom + h + SEPARACION <= vh - MARGEN
    const abajo = pide === 'abajo' ? cabeAbajo || !cabeArriba : !cabeArriba && cabeAbajo
    const top = abajo ? r.bottom + SEPARACION : r.top - h - SEPARACION
    const left = Math.min(Math.max(r.left + r.width / 2 - w / 2, MARGEN), Math.max(MARGEN, vw - w - MARGEN))
    tip.style.top = `${Math.round(top)}px`
    tip.style.left = `${Math.round(left)}px`
    tip.dataset.lado = abajo ? 'abajo' : 'arriba'
    tip.dataset.listo = '1'
  }, [visible])

  if (!visible || typeof document === 'undefined') return null
  return createPortal(
    <div ref={tipRef} id={idRef.current} role="tooltip" className="tip">
      {visible.texto}
      {visible.atajo && <kbd className="tip-atajo">{visible.atajo}</kbd>}
    </div>,
    document.body,
  )
}
